import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import axios from "axios";
import {
  Settings,
  FileText,
  ClipboardCheck,
  FolderOpen,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Loader2,
  ChevronUp,
  ChevronDown,
  Upload,
  Link as LinkIcon,
  File,
  Image,
  Video,
  X,
  MessageCircle,
  Calendar,
  Users,
  Copy,
  CheckCircle,
  Clock,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tipos de perguntas para formulário
const QUESTION_TYPES = [
  { value: "text", label: "Texto curto" },
  { value: "textarea", label: "Texto longo" },
  { value: "select", label: "Seleção única" },
  { value: "radio", label: "Múltipla escolha" },
  { value: "checkbox", label: "Caixas de seleção" },
];

// Periodicidade para presença
const ATTENDANCE_PERIODS = [
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal" },
  { value: "mensal", label: "Mensal" },
  { value: "semestral", label: "Semestral" },
];

// Dias da semana
const WEEKDAYS = [
  { value: 0, label: "Segunda-feira" },
  { value: 1, label: "Terça-feira" },
  { value: 2, label: "Quarta-feira" },
  { value: 3, label: "Quinta-feira" },
  { value: 4, label: "Sexta-feira" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

// Campos disponíveis para inscrição externa
const ENROLLMENT_FIELDS = [
  { value: "email", label: "Email" },
  { value: "data_nascimento", label: "Data de Nascimento" },
  { value: "sexo", label: "Sexo" },
  { value: "endereco", label: "Endereço" },
  { value: "bairro", label: "Bairro" },
  { value: "cidade", label: "Cidade" },
  { value: "estado_civil", label: "Estado Civil" },
  { value: "situacao", label: "Situação" },
  { value: "batismo", label: "Batismo" },
  { value: "ministerios_desejados", label: "Ministérios Desejados" },
  { value: "convidado_nome", label: "Nome do Convidado" },
  { value: "convidado_telefone", label: "Telefone do Convidado" },
  { value: "convidado_email", label: "E-mail do Convidado" },
];

export default function ProjectSettingsModal({ 
  open, 
  onOpenChange, 
  project,
  onSettingsUpdated 
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("geral");
  
  // Settings state
  const [settings, setSettings] = useState({
    formulario_habilitado: false,
    presenca_habilitada: false,
    material_habilitado: false,
    observacoes_aluno: ""
  });
  
  // Enrollment config state (from project)
  const [enrollmentConfig, setEnrollmentConfig] = useState({
    enviar_confirmacao_whatsapp: false,
    disponibilizar_escala_ao_aprovar: false,
    visivel_filtro_pessoas: false,
    conta_conexao: false,
    campos_inscricao_externa: [],
    dias_horarios: ""
  });
  
  // Form state
  const [form, setForm] = useState({
    titulo: "Formulário de Inscrição",
    descricao: "",
    perguntas: [],
    ativo: true
  });
  
  // Attendance config state
  const [attendanceConfig, setAttendanceConfig] = useState({
    periodicidade: "semanal",
    repeticoes: 12,
    data_inicio: new Date().toISOString().split('T')[0],
    horario: "19:00",
    dia_semana: null  // 0-6 (segunda a domingo) - usado para quinzenal/semanal
  });
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    titulo: "",
    descricao: "",
    tipo: "link",
    url: ""
  });

  useEffect(() => {
    if (open && project?.id) {
      loadSettings();
      // Load enrollment config from project
      setEnrollmentConfig({
        enviar_confirmacao_whatsapp: project.enviar_confirmacao_whatsapp || false,
        disponibilizar_escala_ao_aprovar: project.disponibilizar_escala_ao_aprovar || false,
        visivel_filtro_pessoas: project.visivel_filtro_pessoas || false,
        conta_conexao: project.conta_conexao || false,
        campos_inscricao_externa: project.campos_inscricao_externa || [],
        dias_horarios: project.dias_horarios || ""
      });
      // Reset active tab when modal opens
      setActiveTab("geral");
    }
  }, [open, project?.id]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load settings
      const settingsRes = await axios.get(`${API}/projects/${project.id}/settings`);
      setSettings(settingsRes.data || {
        formulario_habilitado: false,
        presenca_habilitada: false,
        material_habilitado: false,
        observacoes_aluno: ""
      });
      
      // Load form
      const formRes = await axios.get(`${API}/projects/${project.id}/form`);
      if (formRes.data && formRes.data.perguntas) {
        setForm(formRes.data);
      }
      
      // Load attendance config
      const attendanceRes = await axios.get(`${API}/projects/${project.id}/attendance/config`);
      if (attendanceRes.data) {
        setAttendanceConfig(attendanceRes.data);
      }
      
      // Load materials
      try {
        const materialsRes = await axios.get(`${API}/projects/${project.id}/materials`);
        setMaterials(materialsRes.data || []);
      } catch (e) {
        // Might fail if not enabled yet
        setMaterials([]);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Save module settings
      await axios.put(`${API}/projects/${project.id}/settings`, settings);
      
      // Also update project enrollment config using dedicated endpoint
      await axios.put(`${API}/projects/${project.id}/enrollment-config`, enrollmentConfig);
      
      toast.success("Configurações salvas!");
      onSettingsUpdated?.();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  // ============== FORM FUNCTIONS ==============
  
  const addQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      tipo: "text",
      titulo: "",
      descricao: "",
      obrigatoria: false,
      opcoes: [],
      ordem: form.perguntas.length
    };
    setForm(prev => ({
      ...prev,
      perguntas: [...prev.perguntas, newQuestion]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setForm(prev => {
      const perguntas = [...prev.perguntas];
      perguntas[index] = { ...perguntas[index], [field]: value };
      return { ...prev, perguntas };
    });
  };

  const removeQuestion = (index) => {
    setForm(prev => ({
      ...prev,
      perguntas: prev.perguntas.filter((_, i) => i !== index)
    }));
  };

  const moveQuestion = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form.perguntas.length) return;
    
    setForm(prev => {
      const perguntas = [...prev.perguntas];
      [perguntas[index], perguntas[newIndex]] = [perguntas[newIndex], perguntas[index]];
      return { ...prev, perguntas };
    });
  };

  const addOption = (questionIndex) => {
    setForm(prev => {
      const perguntas = [...prev.perguntas];
      const opcoes = perguntas[questionIndex].opcoes || [];
      perguntas[questionIndex].opcoes = [...opcoes, ""];
      return { ...prev, perguntas };
    });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setForm(prev => {
      const perguntas = [...prev.perguntas];
      perguntas[questionIndex].opcoes[optionIndex] = value;
      return { ...prev, perguntas };
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    setForm(prev => {
      const perguntas = [...prev.perguntas];
      perguntas[questionIndex].opcoes = perguntas[questionIndex].opcoes.filter((_, i) => i !== optionIndex);
      return { ...prev, perguntas };
    });
  };

  const saveForm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${project.id}/form`, {
        ...form,
        projeto_id: project.id
      });
      toast.success("Formulário salvo!");
    } catch (error) {
      toast.error("Erro ao salvar formulário");
    } finally {
      setSaving(false);
    }
  };

  // ============== ATTENDANCE FUNCTIONS ==============
  
  const saveAttendanceConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/attendance/config`, {
        ...attendanceConfig,
        projeto_id: project.id
      });
      toast.success(`Presença configurada! ${response.data.datas?.length || 0} encontros gerados.`);
    } catch (error) {
      toast.error("Erro ao salvar configuração de presença");
    } finally {
      setSaving(false);
    }
  };

  // ============== MATERIALS FUNCTIONS ==============
  
  const addMaterial = async () => {
    if (!newMaterial.titulo) {
      toast.error("Informe o título do material");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${project.id}/materials`, {
        ...newMaterial,
        projeto_id: project.id,
        ordem: materials.length
      });
      toast.success("Material adicionado!");
      setNewMaterial({ titulo: "", descricao: "", tipo: "link", url: "" });
      loadSettings(); // Reload materials
    } catch (error) {
      toast.error("Erro ao adicionar material");
    } finally {
      setSaving(false);
    }
  };

  const deleteMaterial = async (materialId) => {
    if (!window.confirm("Remover este material?")) return;
    
    try {
      await axios.delete(`${API}/projects/${project.id}/materials/${materialId}`);
      toast.success("Material removido!");
      setMaterials(prev => prev.filter(m => m.id !== materialId));
    } catch (error) {
      toast.error("Erro ao remover material");
    }
  };

  const getMaterialIcon = (tipo) => {
    switch (tipo) {
      case "pdf": return <File className="h-4 w-4 text-red-500" />;
      case "doc": return <FileText className="h-4 w-4 text-blue-500" />;
      case "image": return <Image className="h-4 w-4 text-green-500" />;
      case "video": return <Video className="h-4 w-4 text-purple-500" />;
      default: return <LinkIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="project-settings-modal">
        <DialogHeader className="shrink-0">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            <DialogTitle>Configurações do Curso</DialogTitle>
          </div>
          <p className="text-sm text-muted-foreground">{project?.titulo || project?.nome}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
            <TabsList className="mx-4 mt-2 grid grid-cols-4 shrink-0">
              <TabsTrigger value="geral" className="text-xs" data-testid="tab-geral">
                <Settings className="h-3 w-3 mr-1" />
                Geral
              </TabsTrigger>
              <TabsTrigger value="formulario" className="text-xs" data-testid="tab-formulario" disabled={!settings.formulario_habilitado}>
                <FileText className="h-3 w-3 mr-1" />
                Formulário
              </TabsTrigger>
              <TabsTrigger value="presenca" className="text-xs" data-testid="tab-presenca" disabled={!settings.presenca_habilitada}>
                <ClipboardCheck className="h-3 w-3 mr-1" />
                Presença
              </TabsTrigger>
              <TabsTrigger value="material" className="text-xs" data-testid="tab-material" disabled={!settings.material_habilitado}>
                <FolderOpen className="h-3 w-3 mr-1" />
                Materiais
              </TabsTrigger>
            </TabsList>

            <div className="flex-1">
              {/* TAB GERAL */}
              <TabsContent value="geral" className="p-4 space-y-6 m-0">
                {/* Informações da inscrição */}
                <div className="p-3 rounded-xl border bg-muted/30 space-y-2" data-testid="settings-project-info">
                  <p className="text-sm font-medium flex items-center gap-2"><Info className="h-4 w-4 text-primary" /> Informações</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Status: </span>{project?.status || "-"}</div>
                    {(project?.lideres?.length > 0 || project?.lider_nome) && (
                      <div><span className="text-muted-foreground">Líder(es): </span>{project?.lideres?.length > 0 ? project.lideres.map(l => l.nome).join(", ") : project?.lider_nome}</div>
                    )}
                    {project?.data_inicio && <div><span className="text-muted-foreground">Início: </span>{project.data_inicio.split("T")[0].split("-").reverse().join("/")}</div>}
                    {project?.dias_horarios && <div><span className="text-muted-foreground">Dias/Horários: </span>{project.dias_horarios}</div>}
                    {project?.local && <div className="col-span-2"><span className="text-muted-foreground">Local: </span>{project.local}</div>}
                    {project?.objetivo && <div className="col-span-2"><span className="text-muted-foreground">Observações: </span>{project.objetivo}</div>}
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold">Módulos Disponíveis</h3>
                  <p className="text-sm text-muted-foreground">
                    Habilite os módulos que deseja usar neste curso
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="font-medium">Formulário Dinâmico</p>
                          <p className="text-xs text-muted-foreground">
                            Perguntas personalizadas na inscrição
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.formulario_habilitado}
                        data-testid="switch-formulario"
                        onCheckedChange={(v) => setSettings(prev => ({ ...prev, formulario_habilitado: v }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <ClipboardCheck className="h-5 w-5 text-green-500" />
                        <div>
                          <p className="font-medium">Lista de Presença</p>
                          <p className="text-xs text-muted-foreground">
                            Controle de frequência dos alunos
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.presenca_habilitada}
                        data-testid="switch-presenca"
                        onCheckedChange={(v) => setSettings(prev => ({ ...prev, presenca_habilitada: v }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FolderOpen className="h-5 w-5 text-orange-500" />
                        <div>
                          <p className="font-medium">Material de Apoio</p>
                          <p className="text-xs text-muted-foreground">
                            Arquivos e links para os alunos
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.material_habilitado}
                        data-testid="switch-material"
                        onCheckedChange={(v) => setSettings(prev => ({ ...prev, material_habilitado: v }))}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Dias e Horários */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Dias e Horários
                  </Label>
                  <Input
                    placeholder="Ex: Sábados às 10h, Quartas às 19h30..."
                    value={enrollmentConfig.dias_horarios || ""}
                    onChange={(e) => setEnrollmentConfig(prev => ({ ...prev, dias_horarios: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Informação exibida para os inscritos sobre quando acontecem os encontros
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>Observações para os Alunos</Label>
                  <Textarea
                    placeholder="Informações importantes que serão exibidas para os alunos..."
                    value={settings.observacoes_aluno || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, observacoes_aluno: e.target.value }))}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Estas observações aparecerão na área do aluno
                  </p>
                </div>

                <Separator />

                {/* Configurações de Inscrição */}
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Configurações de Inscrição
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="text-sm font-medium">Enviar Confirmação via WhatsApp</p>
                          <p className="text-xs text-muted-foreground">
                            Notificar participante quando inscrito/aprovado
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={enrollmentConfig.enviar_confirmacao_whatsapp}
                        data-testid="switch-whatsapp"
                        onCheckedChange={(v) => setEnrollmentConfig(prev => ({ ...prev, enviar_confirmacao_whatsapp: v }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-purple-500" />
                        <div>
                          <p className="text-sm font-medium">Disponibilizar para Escala ao Aprovar</p>
                          <p className="text-xs text-muted-foreground">
                            Participante aparece automaticamente nas escalas
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={enrollmentConfig.disponibilizar_escala_ao_aprovar}
                        data-testid="switch-escala"
                        onCheckedChange={(v) => setEnrollmentConfig(prev => ({ ...prev, disponibilizar_escala_ao_aprovar: v }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium">Visível no Filtro de Pessoas</p>
                          <p className="text-xs text-muted-foreground">
                            Mostrar este curso como opção de filtro
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={enrollmentConfig.visivel_filtro_pessoas}
                        data-testid="switch-filtro-pessoas"
                        onCheckedChange={(v) => setEnrollmentConfig(prev => ({ ...prev, visivel_filtro_pessoas: v }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-100">
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="text-sm font-medium">Conta como conexão</p>
                          <p className="text-xs text-muted-foreground">
                            Participantes ativos ficam conectados aos líderes (Igreja Conectada)
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={enrollmentConfig.conta_conexao}
                        data-testid="switch-conta-conexao"
                        onCheckedChange={(v) => setEnrollmentConfig(prev => ({ ...prev, conta_conexao: v }))}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Configuração do Formulário Externo */}
                {project?.disponivel_inscricao && (
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Formulário Externo de Inscrição
                    </h3>
                    
                    <div className="p-3 bg-muted rounded-lg space-y-2">
                      <Label className="text-xs text-muted-foreground">Link Público</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          readOnly 
                          value={`${window.location.origin}/inscricao/${project.id}`}
                          className="text-xs"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/inscricao/${project.id}`);
                            toast.success("Link copiado!");
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Campos a solicitar na inscrição</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {ENROLLMENT_FIELDS.map(field => (
                          <div 
                            key={field.value}
                            className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted"
                            onClick={() => {
                              setEnrollmentConfig(prev => ({
                                ...prev,
                                campos_inscricao_externa: prev.campos_inscricao_externa.includes(field.value)
                                  ? prev.campos_inscricao_externa.filter(f => f !== field.value)
                                  : [...prev.campos_inscricao_externa, field.value]
                              }));
                            }}
                          >
                            <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                              enrollmentConfig.campos_inscricao_externa.includes(field.value) 
                                ? 'bg-primary border-primary' 
                                : 'border-muted-foreground'
                            }`}>
                              {enrollmentConfig.campos_inscricao_externa.includes(field.value) && (
                                <CheckCircle className="h-3 w-3 text-primary-foreground" />
                              )}
                            </div>
                            <span className="text-xs">{field.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <Button onClick={saveSettings} disabled={saving} className="w-full" data-testid="save-settings-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Configurações
                </Button>
              </TabsContent>

              {/* TAB FORMULÁRIO */}
              <TabsContent value="formulario" className="p-4 space-y-4 m-0">
                <div className="space-y-3">
                  <Label>Título do Formulário</Label>
                  <Input
                    value={form.titulo}
                    onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Ex: Questionário de Inscrição"
                  />
                </div>

                <div className="space-y-3">
                  <Label>Descrição</Label>
                  <Textarea
                    value={form.descricao || ""}
                    onChange={(e) => setForm(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Instruções para preenchimento..."
                    rows={2}
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Perguntas</h4>
                    <Button size="sm" onClick={addQuestion}>
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>

                  {form.perguntas.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                      <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Nenhuma pergunta cadastrada</p>
                      <Button variant="link" onClick={addQuestion}>
                        Adicionar primeira pergunta
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {form.perguntas.map((pergunta, index) => (
                        <div key={pergunta.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{index + 1}</Badge>
                            <div className="flex-1 flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => moveQuestion(index, -1)}
                                disabled={index === 0}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => moveQuestion(index, 1)}
                                disabled={index === form.perguntas.length - 1}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => removeQuestion(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 sm:col-span-1">
                              <Label className="text-xs">Tipo</Label>
                              <Select
                                value={pergunta.tipo}
                                onValueChange={(v) => updateQuestion(index, "tipo", v)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUESTION_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>
                                      {t.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2 sm:col-span-1 flex items-end">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pergunta.obrigatoria}
                                  onChange={(e) => updateQuestion(index, "obrigatoria", e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Obrigatória</span>
                              </label>
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Pergunta</Label>
                            <Input
                              value={pergunta.titulo}
                              onChange={(e) => updateQuestion(index, "titulo", e.target.value)}
                              placeholder="Digite a pergunta..."
                            />
                          </div>

                          <div>
                            <Label className="text-xs">Descrição (opcional)</Label>
                            <Input
                              value={pergunta.descricao || ""}
                              onChange={(e) => updateQuestion(index, "descricao", e.target.value)}
                              placeholder="Instruções ou explicações..."
                            />
                          </div>

                          {["select", "radio", "checkbox"].includes(pergunta.tipo) && (
                            <div className="space-y-2">
                              <Label className="text-xs">Opções</Label>
                              {(pergunta.opcoes || []).map((opcao, optIndex) => (
                                <div key={optIndex} className="flex items-center gap-2">
                                  <Input
                                    value={opcao}
                                    onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                    placeholder={`Opção ${optIndex + 1}`}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeOption(index, optIndex)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addOption(index)}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Adicionar Opção
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button onClick={saveForm} disabled={saving} className="w-full" data-testid="save-form-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salvar Formulário
                </Button>
              </TabsContent>

              {/* TAB PRESENÇA */}
              <TabsContent value="presenca" className="p-4 space-y-4 m-0">
                <div className="space-y-4">
                  <h4 className="font-semibold">Configuração de Presença</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Periodicidade</Label>
                      <Select
                        value={attendanceConfig.periodicidade}
                        onValueChange={(v) => setAttendanceConfig(prev => ({ ...prev, periodicidade: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ATTENDANCE_PERIODS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Número de Encontros</Label>
                      <Input
                        type="number"
                        min={1}
                        max={52}
                        value={attendanceConfig.repeticoes}
                        onChange={(e) => setAttendanceConfig(prev => ({ ...prev, repeticoes: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data do Primeiro Encontro</Label>
                      <Input
                        type="date"
                        value={attendanceConfig.data_inicio?.split('T')[0] || ""}
                        onChange={(e) => setAttendanceConfig(prev => ({ ...prev, data_inicio: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Horário</Label>
                      <Input
                        type="time"
                        value={attendanceConfig.horario || ""}
                        onChange={(e) => setAttendanceConfig(prev => ({ ...prev, horario: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Dia da semana para quinzenal */}
                  {(attendanceConfig.periodicidade === "quinzenal" || attendanceConfig.periodicidade === "semanal") && (
                    <div className="space-y-2">
                      <Label>Dia da Semana</Label>
                      <Select
                        value={attendanceConfig.dia_semana?.toString() || ""}
                        onValueChange={(v) => setAttendanceConfig(prev => ({ ...prev, dia_semana: parseInt(v) }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o dia" />
                        </SelectTrigger>
                        <SelectContent>
                          {WEEKDAYS.map((d) => (
                            <SelectItem key={d.value} value={d.value.toString()}>
                              {d.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {attendanceConfig.periodicidade === "quinzenal" && (
                        <p className="text-xs text-muted-foreground">
                          Os encontros serão a cada duas semanas no dia selecionado
                        </p>
                      )}
                    </div>
                  )}

                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Serão gerados <strong>{attendanceConfig.repeticoes}</strong> encontros{" "}
                      <strong>{attendanceConfig.periodicidade === "semanal" ? "semanais" : 
                               attendanceConfig.periodicidade === "quinzenal" ? "quinzenais" :
                               attendanceConfig.periodicidade === "mensal" ? "mensais" : "semestrais"}</strong>{" "}
                      a partir de <strong>{attendanceConfig.data_inicio?.split('T')[0]}</strong>
                      {attendanceConfig.dia_semana !== null && (attendanceConfig.periodicidade === "quinzenal" || attendanceConfig.periodicidade === "semanal") && (
                        <> às <strong>{WEEKDAYS.find(d => d.value === attendanceConfig.dia_semana)?.label || ""}</strong></>
                      )}
                    </p>
                  </div>

                  <Button onClick={saveAttendanceConfig} disabled={saving} className="w-full" data-testid="save-attendance-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Salvar Configuração de Presença
                  </Button>
                </div>
              </TabsContent>

              {/* TAB MATERIAIS */}
              <TabsContent value="material" className="p-4 space-y-4 m-0">
                <div className="space-y-4">
                  <h4 className="font-semibold">Adicionar Material</h4>
                  
                  <div className="space-y-3 p-4 border rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label>Título</Label>
                        <Input
                          value={newMaterial.titulo}
                          onChange={(e) => setNewMaterial(prev => ({ ...prev, titulo: e.target.value }))}
                          placeholder="Nome do material"
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <Label>Tipo</Label>
                        <Select
                          value={newMaterial.tipo}
                          onValueChange={(v) => setNewMaterial(prev => ({ ...prev, tipo: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="link">Link</SelectItem>
                            <SelectItem value="pdf">PDF</SelectItem>
                            <SelectItem value="doc">Documento</SelectItem>
                            <SelectItem value="image">Imagem</SelectItem>
                            <SelectItem value="video">Vídeo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <Label>URL</Label>
                        <Input
                          value={newMaterial.url}
                          onChange={(e) => setNewMaterial(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrição (opcional)</Label>
                      <Input
                        value={newMaterial.descricao}
                        onChange={(e) => setNewMaterial(prev => ({ ...prev, descricao: e.target.value }))}
                        placeholder="Breve descrição do material"
                      />
                    </div>
                    <Button onClick={addMaterial} disabled={saving || !newMaterial.titulo}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      Adicionar Material
                    </Button>
                  </div>

                  <Separator />

                  <h4 className="font-semibold">Materiais Cadastrados</h4>
                  
                  {materials.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                      <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Nenhum material cadastrado</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {materials.map((material) => (
                        <div
                          key={material.id}
                          className="flex items-center gap-3 p-3 border rounded-lg"
                        >
                          {getMaterialIcon(material.tipo)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{material.titulo}</p>
                            {material.descricao && (
                              <p className="text-xs text-muted-foreground truncate">
                                {material.descricao}
                              </p>
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {material.tipo}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => deleteMaterial(material.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
